// migrateBorefields — the read-time data migration for the Bore→Well cutover (EPIC Phase 2).
//
// Pure transform: a legacy `Borefield[]` (the `localBorefields` store) → the canonical unified store
// shape `{ pads, wells }`. Each Borefield → one FieldPad (id preserved, so wells link via
// `fieldPadId === borefield.id`); each Bore → one Well + primary Wellbore via the Phase-1 adapter
// (shim identity: wellbore.id == bore.id). NON-DESTRUCTIVE + LOSSLESS — the legacy fields with no
// canonical home are carried as DEEP CLONES (`Pad.legacyBorefield` / `Well.legacyBore`), so the
// facade (`useBorefieldData` over the unified store) projects the legacy Borefield/Bore view back
// with no loss while consumers migrate (Phases 4–6).
//
// MINIMAL PROMOTION (same invariant as Phase 1): `borefieldToPad` promotes ONLY `padName` (a true 1:1
// promotion of `borefieldName`) and carries the rest in `legacyBorefield`. `operatorOfRecord` is set
// too, but it is a DERIVED display value (`operatorName || ownerName`), NOT a 1:1 promotion — so the
// facade reads `borefieldName` back from `pad.padName` (canonical edit wins) but reads
// `operatorName`/`ownerName` back from the CARRY (the originals), which is lossless even when an empty
// operator fell through to `ownerName`. `businessUnit` is deliberately UNSET (the legacy Borefield has
// none). `padToBorefieldView` (below) is the symmetric inverse that the 2c facade wraps.
//
// PURE: no Date.now() / generateId / randomness (audit carried from the source). Unit-testable.
// No engineering/economic/statistical math (Charter Abs-2).

import type { Borefield } from "../borefields/types";
import type { Pad, Well } from "./types";
import { boreToWell, borefieldMetaOf, wellToBoreView } from "./boreToWell";

/** Wrap one legacy Borefield into a canonical FieldPad (id preserved). Promotes padName +
 *  operatorOfRecord + businessUnit; carries everything else as a deep clone in `legacyBorefield`. */
export function borefieldToPad(bf: Borefield): Pad {
  // COORDINATE PROMOTION (the deferred coord phase): a legacy Borefield has no own surface coordinate,
  // so anchor the Pad marker at its FIRST located bore (first valid lat/lon — no centroid, matching the
  // PO 2026-06-09 first-located-bore anchor convention; a copy/parse only, no math,
  // Charter Abs-2). Spine-native, like the Well promotion in boreToWell. Without it, the pad marker was
  // absent from the surface map.
  const anchor = (bf.bores ?? []).find(
    (b) =>
      b.latitude &&
      b.longitude &&
      !isNaN(parseFloat(String(b.latitude))) &&
      !isNaN(parseFloat(String(b.longitude))),
  );
  return {
    id: bf.id, // pad id == borefield id, so migrated wells (fieldPadId == bf.id) link correctly
    // PROMOTED — only `padName` is a TRUE 1:1 promotion: padToBorefieldView reads `borefieldName` back
    // from `pad.padName`, so a rename surfaces in the legacy view. `operatorOfRecord` (below) is
    // DERIVED, not 1:1 — the facade reads operatorName/ownerName from the CARRY, not this field (see
    // the file header + padToBorefieldView). Corrected from the 2a contract, which over-claimed this.
    padName: bf.borefieldName,
    // `||` (NOT `??`): an empty operatorName falls through to the required `ownerName`, so a
    // named-owner borefield never migrates to an empty (invalid) operator. Both survive in the carry.
    operatorOfRecord: bf.operatorName || bf.ownerName || "",
    // businessUnit deliberately UNSET — the legacy Borefield has none; inventing one would fail
    // usePadForm's required-field gate. Absent = "prompt on first edit" (Pad.businessUnit is optional).
    legacyBorefield: borefieldMetaOf(bf),
    ...(anchor
      ? {
          surfaceLatitude: String(anchor.latitude),
          surfaceLongitude: String(anchor.longitude),
        }
      : {}),
    ...(bf.createdAt ? { createdAt: bf.createdAt } : {}),
    ...(bf.createdBy ? { createdBy: bf.createdBy } : {}),
    ...(bf.modifiedAt ? { modifiedAt: bf.modifiedAt } : {}),
    ...(bf.modifiedBy ? { modifiedBy: bf.modifiedBy } : {}),
  };
}

/** Migrate a legacy `Borefield[]` into the canonical unified-store shape. One Pad per Borefield, one
 *  Well (+ primary Wellbore) per Bore. Pure + lossless (legacy data carried for round-trip). */
export function migrateBorefieldsToWells(borefields: readonly Borefield[]): {
  pads: Pad[];
  wells: Well[];
} {
  // Fail-fast (NOT silent wrong-resolution): the shim sets pad.id = borefield.id and
  // wellbore.id = bore.id, so a bore id equal to a borefield id would make a pad + a wellbore share an
  // id. Assert disjoint — the seed corpus (D5) is where hand-assigned non-uuid ids could collide.
  const bfIds = new Set(borefields.map((bf) => bf.id));
  for (const bf of borefields) {
    for (const bore of bf.bores ?? []) {
      if (bfIds.has(bore.id)) {
        throw new Error(
          `migrateBorefieldsToWells: bore id "${bore.id}" collides with a borefield id — pad and ` +
            `wellbore ids must be disjoint (shim identity). Re-id the source before migrating.`,
        );
      }
    }
  }

  const pads: Pad[] = [];
  const wells: Well[] = [];
  for (const bf of borefields) {
    pads.push(borefieldToPad(bf));
    for (const bore of bf.bores ?? []) {
      wells.push(boreToWell(bore, bf).well);
    }
  }
  return { pads, wells };
}

/** The SYMMETRIC INVERSE of the migration — project a FieldPad + its Wells back into a legacy
 *  Borefield view, for consumers that still read the Borefield/Bore shape during the transition (the
 *  `useBorefieldData` facade, 2c). Read-back contract:
 *   - `borefieldName` ← `pad.padName` (a true 1:1 promotion → canonical edit wins, incl. clear-to-"";
 *     `!== undefined` so a deliberate empty surfaces instead of resurrecting the carry).
 *   - `operatorName` / `ownerName` ← the CARRY (the originals). `operatorOfRecord` is a DERIVED display
 *     value (`operatorName || ownerName`), NOT a 1:1 promotion — reconstructing `operatorName` from it
 *     would be lossy (an empty operator round-trips to `ownerName`). So they come from `legacyBorefield`.
 *     CONSEQUENCE (flagged): a *new-model* edit to `pad.operatorOfRecord` does NOT surface in this Bore
 *     view (operatorName always reads the original from the carry) — accepted until a real operatorName
 *     promotion lands. `borefieldName` (1:1) does surface.
 *   - everything else ← the carry; `bores` ← the pad's CARRY-BEARING Wellbores via `wellToBoreView`
 *     (except `jobs`, which `wellToBoreView` reads from the promoted `wellbore.jobs`).
 *
 *  ONE BORE PER CARRY-BEARING WELLBORE (not per Well): the legacy flat model had no wellbore tier, so
 *  every drilled path (original hole + each sidetrack) was its own Bore row — hence the flatMap, not
 *  `wellbores[0]`. Collapsing to the primary would silently drop sidetracks (Charter Absolute #7/#10).
 *
 *  INTENTIONALLY OMITTED (explicit facade contract, NOT silent loss): Well-native wellbores with no
 *  `legacyBore` carry — wizard-created after the cutover — have no legacy Bore form, so they cannot be
 *  projected and are absent from this transitional Bore view; they remain in the unified store and are
 *  reached via the new Well UI. A wellbore-less Well likewise contributes nothing. (Pinned by tests.)
 *
 *  MUTATION-ISOLATED: the result is `structuredClone`d on output, so a legacy consumer that mutates the
 *  returned Borefield in place (the legacy pattern) can't write through into the stored `legacy*`
 *  carries — symmetric with `boreToWell`'s clone-on-input (Charter Absolute #7). The 2c hook MUST
 *  memoize this call so the clone is paid once per store change, not per render.
 *
 *  ORDER: `bores` follow the order of `wells` (filtered to this pad). The migration emits wells in bore
 *  order, so an immediate migrate→project is identity; thereafter the view reflects the store's order.
 *
 *  THROWS for a pad with no legacy carry (a wizard-created pad) — failing fast beats inventing the
 *  legacy meta, symmetric with `wellToBoreView` (Charter Absolute #4). The `legacy*` carries (and this
 *  whole facade) are deleted in Phase 7. */
export function padToBorefieldView(
  pad: Pad,
  wells: readonly Well[],
): Borefield {
  const meta = pad.legacyBorefield;
  if (!meta) {
    throw new Error(
      "padToBorefieldView: this FieldPad has no legacyBorefield carry — a Borefield view of a " +
        "wizard-created pad is not supported until a later cutover phase promotes the canonical " +
        "fields (EPIC_BORE_TO_WELL_CUTOVER.md). Do not call padToBorefieldView on a Pad-native field.",
    );
  }
  const bores = wells
    .filter((w) => w.fieldPadId === pad.id)
    .flatMap((w) =>
      w.wellbores
        // carry-bearing only — a carry-less (wizard) wellbore has no legacy Bore form (and
        // wellToBoreView would throw on it). Omission is the documented contract above, not silent loss.
        .filter((wb) => wb.legacyBore !== undefined)
        .map((wb) => wellToBoreView(w, wb)),
    );
  return structuredClone({
    ...meta,
    id: pad.id,
    // 1:1 promotion read back from the canonical home (symmetric with borefieldToPad's padName).
    borefieldName: pad.padName !== undefined ? pad.padName : meta.borefieldName,
    bores,
  });
}
